// ─────────────────────────────────────────────
//  src/pages/Portfolio.js  —  Phase 5
//  Full portfolio tracker:
//  • Summary stat cards (invested, value, P&L, day change)
//  • Holdings table with live CMP, P&L, weight bar
//  • Add BUY/SELL transaction modal
//  • Excel/CSV import via ExcelImporter
//  • Allocation pie chart + P&L bar chart (Recharts)
//  • Transaction history log
//  • Sort by any column
// ─────────────────────────────────────────────
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  PieChart, Pie, Cell, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { usePortfolio } from "../hooks/usePortfolio";
import ExcelImporter    from "../components/portfolio/ExcelImporter";
import "./Portfolio.css";

// ── Formatters ────────────────────────────────
function fmtINR(n, compact = false) {
  const abs = Math.abs(n);
  if (compact) {
    if (abs >= 1e7) return "₹" + (n / 1e7).toFixed(2) + " Cr";
    if (abs >= 1e5) return "₹" + (n / 1e5).toFixed(2) + " L";
    if (abs >= 1e3) return "₹" + (n / 1e3).toFixed(1) + " K";
  }
  return "₹" + Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(n) {
  return (n >= 0 ? "+" : "") + Number(n).toFixed(2) + "%";
}

const PIE_COLORS = [
  "#3b82f6","#22c55e","#a78bfa","#fbbf24","#f87171","#34d399",
  "#60a5fa","#fb923c","#e879f9","#38bdf8","#4ade80","#facc15",
];

// ── Add/Edit Transaction Modal ────────────────
function TransactionModal({ onSave, onClose, preSymbol = "" }) {
  const [form, setForm] = useState({
    type: "BUY", symbol: preSymbol, exchange: "NSE",
    qty: "", price: "", date: new Date().toISOString().slice(0, 10),
    brokerage: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    const sym = form.symbol.trim().toUpperCase();
    const qty = parseFloat(form.qty);
    const prc = parseFloat(form.price);
    if (!sym)        return setErr("Symbol is required.");
    if (!qty || qty <= 0) return setErr("Quantity must be > 0.");
    if (!prc || prc <= 0) return setErr("Price must be > 0.");
    setSaving(true);
    try {
      await onSave({ ...form, symbol: sym, qty, price: prc, brokerage: parseFloat(form.brokerage) || 0 });
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="port-modal-overlay" onClick={onClose}>
      <div className="port-modal" onClick={e => e.stopPropagation()}>
        <div className="port-modal-header">
          <h3>Add Transaction</h3>
          <button className="port-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="port-modal-body">
          {/* BUY / SELL toggle */}
          <div className="port-field">
            <label>Transaction Type</label>
            <div className="port-type-toggle">
              <button className={`port-type-opt buy  ${form.type === "BUY"  ? "active" : ""}`} onClick={() => set("type","BUY")}>🟢 BUY</button>
              <button className={`port-type-opt sell ${form.type === "SELL" ? "active" : ""}`} onClick={() => set("type","SELL")}>🔴 SELL</button>
            </div>
          </div>

          {/* Symbol + Exchange */}
          <div className="port-field-row">
            <div className="port-field">
              <label>Symbol *</label>
              <input placeholder="e.g. RELIANCE" value={form.symbol}
                onChange={e => set("symbol", e.target.value.toUpperCase())} />
            </div>
            <div className="port-field">
              <label>Exchange</label>
              <select value={form.exchange} onChange={e => set("exchange", e.target.value)}>
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
              </select>
            </div>
          </div>

          {/* Qty + Price */}
          <div className="port-field-row">
            <div className="port-field">
              <label>Quantity *</label>
              <input type="number" min="0" placeholder="10" value={form.qty}
                onChange={e => set("qty", e.target.value)} />
            </div>
            <div className="port-field">
              <label>Price per Share (₹) *</label>
              <input type="number" min="0" step="0.01" placeholder="2800.00" value={form.price}
                onChange={e => set("price", e.target.value)} />
            </div>
          </div>

          {/* Date + Brokerage */}
          <div className="port-field-row">
            <div className="port-field">
              <label>Trade Date</label>
              <input type="date" value={form.date} onChange={e => set("date", e.target.value)} />
            </div>
            <div className="port-field">
              <label>Brokerage (₹)</label>
              <input type="number" min="0" step="0.01" placeholder="0" value={form.brokerage}
                onChange={e => set("brokerage", e.target.value)} />
            </div>
          </div>

          {/* Notes */}
          <div className="port-field">
            <label>Notes</label>
            <input placeholder="Optional notes…" value={form.notes}
              onChange={e => set("notes", e.target.value)} />
          </div>

          {/* Amount preview */}
          {form.qty && form.price && (
            <div style={{ padding: "10px 12px", background: "rgba(255,255,255,0.03)", borderRadius: 8, fontSize: "0.82rem" }}>
              <span style={{ color: "var(--siq-muted)" }}>Total value: </span>
              <span style={{ fontFamily: "var(--siq-mono)", fontWeight: 700, color: "var(--siq-text)" }}>
                {fmtINR(parseFloat(form.qty || 0) * parseFloat(form.price || 0))}
              </span>
            </div>
          )}

          {err && <div style={{ color: "var(--siq-red)", fontSize: "0.8rem" }}>⚠ {err}</div>}
        </div>

        <div className="port-modal-footer">
          <button className="port-btn port-btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="port-btn port-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : `Confirm ${form.type}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Custom Pie tooltip ────────────────────────
function PieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div style={{ background: "#0e1929", border: "1px solid #1e2d45", borderRadius: 8, padding: "8px 12px" }}>
      <div style={{ fontWeight: 700, color: "#e2e8f0", fontSize: "0.82rem" }}>{d.name}</div>
      <div style={{ color: "#64748b", fontSize: "0.75rem" }}>{fmtINR(d.value, true)} · {d.payload.pct.toFixed(1)}%</div>
    </div>
  );
}

// ── Main Portfolio Page ───────────────────────
export default function Portfolio() {
  const navigate = useNavigate();
  const {
    holdings, transactions, summary, loading, priceLoading,
    addTransaction, removeHolding, importHoldings, refreshPrices,
  } = usePortfolio();

  const [showAddModal,   setShowAddModal]   = useState(false);
  const [showImporter,   setShowImporter]   = useState(false);
  const [showTxHistory,  setShowTxHistory]  = useState(false);
  const [activeTab,      setActiveTab]      = useState("holdings"); // holdings | transactions
  const [sortBy,         setSortBy]         = useState("current");
  const [sortDir,        setSortDir]        = useState("desc");
  const [preSymbol,      setPreSymbol]      = useState("");

  // ── Sort holdings ──────────────────────────
  const sortedHoldings = useMemo(() => {
    const arr = [...holdings];
    arr.sort((a, b) => {
      const mult = sortDir === "asc" ? 1 : -1;
      const va = a[sortBy] ?? 0;
      const vb = b[sortBy] ?? 0;
      return typeof va === "string"
        ? va.localeCompare(vb) * mult
        : (va - vb) * mult;
    });
    return arr;
  }, [holdings, sortBy, sortDir]);

  function handleSort(field) {
    if (sortBy === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(field); setSortDir("desc"); }
  }

  const thCls = (f) => sortBy === f ? `sort-${sortDir}` : "";

  // ── Charts data ────────────────────────────
  const totalVal = summary.totalCurrent || 1;

  const pieData = sortedHoldings
    .filter(h => h.current > 0)
    .map((h, i) => ({
      name: h.symbol,
      value: h.current,
      pct: (h.current / totalVal) * 100,
      color: PIE_COLORS[i % PIE_COLORS.length],
    }));

  const barData = sortedHoldings
    .filter(h => h.invested > 0)
    .map(h => ({ name: h.symbol, pnl: +h.pnl.toFixed(0), pct: +h.pnlPct.toFixed(2) }))
    .sort((a, b) => b.pnl - a.pnl);

  // ── TX history for active symbol / all ────
  const sortedTx = [...transactions].sort((a, b) =>
    new Date(b.created_at || b.date) - new Date(a.created_at || a.date)
  );

  // ── Loading state ──────────────────────────
  if (loading) {
    return (
      <div className="port-page">
        <div className="port-skel">
          {[1,2,3,4,5,6].map(i => <div key={i} className="port-skel-row" />)}
        </div>
      </div>
    );
  }

  const isUp      = summary.totalPnL >= 0;
  const isDayUp   = summary.totalDayPnl >= 0;
  const hasData   = holdings.length > 0;

  return (
    <div className="port-page">

      {/* ── Page Header ── */}
      <div className="port-header">
        <div className="port-header-left">
          <h1>💼 My Portfolio</h1>
          <p>
            {hasData
              ? `${holdings.length} holdings · Last refreshed just now`
              : "No holdings yet — add manually or import from Excel"}
          </p>
        </div>
        <div className="port-header-actions">
          {hasData && (
            <button className="port-btn port-btn-ghost" onClick={refreshPrices} title="Refresh live prices">
              {priceLoading ? "⟳ Refreshing…" : "⟳ Refresh"}
            </button>
          )}
          <button className="port-btn port-btn-secondary" onClick={() => setShowImporter(true)}>
            📥 Import Excel
          </button>
          <button className="port-btn port-btn-primary" onClick={() => { setPreSymbol(""); setShowAddModal(true); }}>
            ＋ Add Trade
          </button>
        </div>
      </div>

      {/* ── Summary Cards ── */}
      <div className="port-summary-grid">
        <div className="port-stat-card" style={{ "--port-accent": "#3b82f6" }}>
          <div className="port-stat-label">Total Invested</div>
          <div className="port-stat-value">{fmtINR(summary.totalInvested, true)}</div>
          <div className="port-stat-sub">{holdings.length} holdings</div>
        </div>

        <div className="port-stat-card" style={{ "--port-accent": "#a78bfa" }}>
          <div className="port-stat-label">Current Value</div>
          <div className="port-stat-value">{fmtINR(summary.totalCurrent, true)}</div>
          <div className="port-stat-sub">Live prices</div>
        </div>

        <div className="port-stat-card" style={{ "--port-accent": isUp ? "#22c55e" : "#ef4444" }}>
          <div className="port-stat-label">Overall P&L</div>
          <div className="port-stat-value" style={{ color: isUp ? "var(--siq-green)" : "var(--siq-red)" }}>
            {fmtINR(summary.totalPnL, true)}
          </div>
          <div className={`port-stat-sub ${isUp ? "up" : "down"}`}>
            {fmtPct(summary.totalPnLPercent)} all-time
          </div>
        </div>

        <div className="port-stat-card" style={{ "--port-accent": isDayUp ? "#22c55e" : "#ef4444" }}>
          <div className="port-stat-label">Today's P&L</div>
          <div className="port-stat-value" style={{ color: isDayUp ? "var(--siq-green)" : "var(--siq-red)" }}>
            {fmtINR(summary.totalDayPnl, true)}
          </div>
          <div className={`port-stat-sub ${isDayUp ? "up" : "down"}`}>
            {fmtPct(summary.dayPnLPercent)} today
          </div>
        </div>
      </div>

      {/* ── Main Grid ── */}
      <div className="port-main-grid">

        {/* ── Left: Table ── */}
        <div className="port-panel">
          {/* Tab bar */}
          <div className="port-panel-header">
            <div style={{ display: "flex", gap: 0 }}>
              {["holdings","transactions"].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    padding: "6px 14px", fontSize: "0.82rem", fontWeight: 600,
                    fontFamily: "var(--siq-font)",
                    color: activeTab === tab ? "var(--siq-text)" : "var(--siq-muted)",
                    borderBottom: activeTab === tab ? "2px solid var(--siq-blue)" : "2px solid transparent",
                    transition: "all 0.15s",
                  }}
                >
                  {tab === "holdings" ? `Holdings (${holdings.length})` : `Transactions (${transactions.length})`}
                </button>
              ))}
            </div>
          </div>

          {/* ── Holdings table ── */}
          {activeTab === "holdings" && (
            <>
              {!hasData ? (
                <div className="port-empty">
                  <div className="port-empty-icon">📊</div>
                  <p className="port-empty-title">No Holdings Yet</p>
                  <p className="port-empty-sub">
                    Add your first trade manually or import your portfolio from an Excel/CSV file.
                  </p>
                  <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                    <button className="port-btn port-btn-secondary" onClick={() => setShowImporter(true)}>📥 Import Excel</button>
                    <button className="port-btn port-btn-primary" onClick={() => setShowAddModal(true)}>＋ Add Trade</button>
                  </div>
                </div>
              ) : (
                <div className="port-table-wrap">
                  <table className="port-table">
                    <thead>
                      <tr>
                        <th className={thCls("symbol")}    onClick={() => handleSort("symbol")}>Stock</th>
                        <th className={thCls("qty")}       onClick={() => handleSort("qty")}>Qty</th>
                        <th className={thCls("avg_buy_price")} onClick={() => handleSort("avg_buy_price")}>Avg Buy</th>
                        <th className={thCls("cmp")}       onClick={() => handleSort("cmp")}>CMP</th>
                        <th className={thCls("current")}   onClick={() => handleSort("current")}>Value</th>
                        <th className={thCls("pnl")}       onClick={() => handleSort("pnl")}>P&L</th>
                        <th className={thCls("dayChgPct")} onClick={() => handleSort("dayChgPct")}>Day</th>
                        <th>Weight</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedHoldings.map(h => {
                        const weight = totalVal > 0 ? (h.current / totalVal) * 100 : 0;
                        return (
                          <tr key={h.id}>
                            {/* Symbol */}
                            <td>
                              <div className="port-sym-cell">
                                <div className="port-sym">
                                  <a onClick={() => navigate(`/stock/${h.symbol}`, { state: { exchange: h.exchange } })}>
                                    {h.symbol}
                                  </a>
                                </div>
                                <div className="port-exch">{h.exchange}</div>
                              </div>
                            </td>
                            {/* Qty */}
                            <td className="port-mono">{h.qty.toLocaleString("en-IN")}</td>
                            {/* Avg Buy */}
                            <td className="port-mono">{fmtINR(h.avg_buy_price)}</td>
                            {/* CMP */}
                            <td className="port-mono">
                              {h.cmp > 0 ? fmtINR(h.cmp) : <span style={{ color: "var(--siq-muted)" }}>–</span>}
                            </td>
                            {/* Current Value */}
                            <td className="port-mono">{h.current > 0 ? fmtINR(h.current, true) : "–"}</td>
                            {/* P&L */}
                            <td>
                              <div className="port-pnl-cell">
                                <span className={`port-pnl-amt ${h.pnl >= 0 ? "text-up" : "text-down"}`}>
                                  {h.pnl >= 0 ? "+" : ""}{fmtINR(h.pnl, true)}
                                </span>
                                <span className={`port-pnl-pct ${h.pnl >= 0 ? "text-up" : "text-down"}`}>
                                  {fmtPct(h.pnlPct)}
                                </span>
                              </div>
                            </td>
                            {/* Day change */}
                            <td className={`port-mono ${h.dayChgPct >= 0 ? "text-up" : "text-down"}`}>
                              {h.dayChgPct !== 0 ? fmtPct(h.dayChgPct) : "–"}
                            </td>
                            {/* Weight bar */}
                            <td>
                              <div className="port-weight-wrap">
                                <div className="port-weight-bar">
                                  <div className="port-weight-fill" style={{ width: `${Math.min(weight, 100)}%` }} />
                                </div>
                                <div className="port-weight-val">{weight.toFixed(1)}%</div>
                              </div>
                            </td>
                            {/* Row actions */}
                            <td>
                              <div className="port-row-actions">
                                <button className="port-row-btn analyze" title="Analyze"
                                  onClick={() => navigate(`/stock/${h.symbol}`, { state: { exchange: h.exchange } })}>
                                  📈
                                </button>
                                <button className="port-row-btn" title="Add trade"
                                  onClick={() => { setPreSymbol(h.symbol); setShowAddModal(true); }}>
                                  ＋
                                </button>
                                <button className="port-row-btn del" title="Remove holding"
                                  onClick={() => window.confirm(`Remove ${h.symbol} from portfolio?`) && removeHolding(h.id)}>
                                  🗑
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ── Transactions tab ── */}
          {activeTab === "transactions" && (
            <div className="port-tx-list">
              {!sortedTx.length ? (
                <div className="port-empty" style={{ padding: "40px 24px" }}>
                  <div className="port-empty-icon">📋</div>
                  <p className="port-empty-sub">No transactions yet.</p>
                </div>
              ) : (
                sortedTx.map(tx => (
                  <div key={tx.id} className="port-tx-item" style={{ padding: "9px 18px" }}>
                    <span className={`port-tx-type ${tx.type?.toLowerCase()}`}>{tx.type}</span>
                    <span className="port-tx-sym">{tx.symbol}</span>
                    <div className="port-tx-meta">
                      {tx.qty} shares @ {fmtINR(tx.price)}
                      {tx.date ? ` · ${tx.date}` : ""}
                      {tx.source === "excel_import" ? " · 📥 imported" : ""}
                    </div>
                    <span className="port-tx-val">{fmtINR(tx.qty * tx.price, true)}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* ── Right: Charts ── */}
        <div className="port-charts-col">

          {/* Allocation Pie */}
          <div className="port-chart-panel">
            <div className="port-chart-title">Allocation</div>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      dataKey="value" paddingAngle={2}>
                      {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <RTooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                {/* Legend */}
                <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 4 }}>
                  {pieData.slice(0, 6).map((d, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.75rem" }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
                      <span style={{ color: "#94a3b8", flex: 1 }}>{d.name}</span>
                      <span style={{ color: "#64748b", fontFamily: "var(--siq-mono)" }}>{d.pct.toFixed(1)}%</span>
                    </div>
                  ))}
                  {pieData.length > 6 && (
                    <div style={{ fontSize: "0.7rem", color: "var(--siq-muted)" }}>+{pieData.length - 6} more</div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ color: "var(--siq-muted)", fontSize: "0.8rem", textAlign: "center", padding: "40px 0" }}>
                No data yet
              </div>
            )}
          </div>

          {/* P&L Bar Chart */}
          <div className="port-chart-panel">
            <div className="port-chart-title">P&L per Stock</div>
            {barData.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(barData.length * 32, 120)}>
                <BarChart data={barData} layout="vertical" margin={{ left: 8, right: 8, top: 4, bottom: 4 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={72}
                    tick={{ fill: "#64748b", fontSize: 11, fontFamily: "var(--siq-mono)" }} />
                  <RTooltip
                    formatter={(v, n) => [fmtINR(v, true), "P&L"]}
                    contentStyle={{ background: "#0e1929", border: "1px solid #1e2d45", borderRadius: 8, fontSize: "0.78rem" }}
                  />
                  <ReferenceLine x={0} stroke="#1e2d45" />
                  <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                    {barData.map((d, i) => (
                      <Cell key={i} fill={d.pnl >= 0 ? "#22c55e" : "#ef4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ color: "var(--siq-muted)", fontSize: "0.8rem", textAlign: "center", padding: "40px 0" }}>
                No live data yet
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Modals ── */}
      {showAddModal && (
        <TransactionModal
          onSave={addTransaction}
          onClose={() => setShowAddModal(false)}
          preSymbol={preSymbol}
        />
      )}

      {showImporter && (
        <ExcelImporter
          onImport={importHoldings}
          onClose={() => setShowImporter(false)}
        />
      )}
    </div>
  );
}